// Utilitários do motor de alertas (PO1-2194), compartilhados pelo sino do
// cabeçalho, por "Meus alertas", "Minhas tarefas" e pelas telas de admin.
//
// Este arquivo NÃO importa routes/products.js de propósito: products.js puxa
// todas as telas do Hub, e o sino (que vive no cabeçalho) e os testes dele não
// precisam disso. O sentido é o contrário: products.js importa daqui.
//
// Sem `?.`/`??`: o build roda em CRA 3.4 / Node 12.

// Produtos que têm "Meus alertas" / "Minhas tarefas" no menu, com o slug do path
// (/app/<slug>/alertas). A ordem é a de PRODUCT_ORDER e serve de fallback quando
// o produto ativo não tem alertas (ex.: EVERLOG_ADMIN).
export const ALERTAS_SLUG_POR_PRODUTO = {
  FLOW: "flow",
  GOBID: "gobid",
  TRACKFY: "trackfy",
  AUTOPILOT: "autopilot",
  MEUFRETE: "meufrete",
};

const ORDEM_PRODUTOS_ALERTAS = ["FLOW", "GOBID", "TRACKFY", "AUTOPILOT", "MEUFRETE"];

export const PRODUTO_LABEL = {
  FLOW: "Flow",
  GOBID: "GoBid",
  TRACKFY: "Trackfy",
  AUTOPILOT: "Autopilot",
  MEUFRETE: "MeuFrete",
};

export const CANAL_LABEL = {
  SINO: "Sino",
  EMAIL: "E-mail",
  AMBOS: "Sino e e-mail",
};

export const FREQUENCIA_LABEL = {
  IMEDIATO: "Na hora",
  RESUMO_DIARIO: "Resumo diário às 7h",
};

// Frequência por canal (PO1-3249): cada canal da inscrição tem a própria
// escolha. "" = canal desligado; o valor vai como null para o backend.
export const CANAL_DESLIGADO = "";
export const FREQUENCIA_CANAL_LABEL = {
  [CANAL_DESLIGADO]: "Desligado",
  IMEDIATO: "Na hora",
  RESUMO_DIARIO: "Resumo diário",
};

export const TIPO_LABEL = {
  NOTIFICACAO: "Notificação",
  TAREFA: "Tarefa",
  RELATORIO: "Relatório",
};

// Relatório (PO1-3384): um e-mail por embarcador, com anexo, uma vez por dia
// útil. Só e-mail, sem sino; nas telas fica num grupo próprio com liga/desliga.
export function ehRelatorio(alerta) {
  return !!alerta && alerta.tipo === "RELATORIO";
}

// Inscrição de relatório: só o e-mail, na frequência do catálogo ("na hora" =
// quando o job roda). O backend recusa sino para esse tipo.
export function inscricaoRelatorio(alerta) {
  return normalizarInscricao({ sino: null, email: frequenciaPadraoDe(alerta) });
}

export function rotaAlertas(codigo) {
  const slug = ALERTAS_SLUG_POR_PRODUTO[codigo];
  return slug ? `/app/${slug}/alertas` : null;
}

export function rotaTarefas(codigo) {
  const slug = ALERTAS_SLUG_POR_PRODUTO[codigo];
  return slug ? `/app/${slug}/tarefas` : null;
}

// Produto em que "Meus alertas" / "Minhas tarefas" devem abrir: o ativo, se ele
// tem as telas; senão o primeiro contratado que tem (o admin Everlog costuma estar
// em EVERLOG_ADMIN, que não tem essas telas). `codes` é o Set de useFeatures().
export function produtoComAlertas(activeProduct, codes) {
  if (activeProduct && ALERTAS_SLUG_POR_PRODUTO[activeProduct]) return activeProduct;
  if (codes && typeof codes.has === "function") {
    const achado = ORDEM_PRODUTOS_ALERTAS.find((c) => codes.has(c));
    if (achado) return achado;
  }
  return null;
}

// Canais que o usuário pode escolher, a partir dos permitidos no catálogo: com
// SINO e EMAIL permitidos, oferece também AMBOS.
export function opcoesCanal(canaisPermitidos) {
  const permitidos = canaisPermitidos || [];
  const out = [];
  if (permitidos.indexOf("SINO") !== -1) out.push("SINO");
  if (permitidos.indexOf("EMAIL") !== -1) out.push("EMAIL");
  if (out.length === 2) out.push("AMBOS");
  return out;
}

// Frequência padrão do catálogo (ou a primeira permitida, se a padrão vier fora
// da lista).
export function frequenciaPadraoDe(alerta) {
  const freqs = (alerta && alerta.frequenciasPermitidas) || [];
  const padrao = alerta && alerta.frequenciaPadrao;
  return padrao && freqs.indexOf(padrao) !== -1 ? padrao : freqs[0] || "IMEDIATO";
}

// Opções do select de um canal (SINO ou EMAIL): "Desligado" + as frequências
// permitidas no catálogo. Canal não permitido só tem "Desligado".
export function opcoesFrequenciaCanal(alerta, canal) {
  const permitidos = (alerta && alerta.canaisPermitidos) || [];
  // As telas usam "sino"/"email" (chaves do payload); o catálogo usa SINO/EMAIL.
  if (permitidos.indexOf(String(canal || "").toUpperCase()) === -1) return [CANAL_DESLIGADO];
  return [CANAL_DESLIGADO].concat((alerta && alerta.frequenciasPermitidas) || []);
}

// Inscrição padrão ao ligar um alerta sem escolha prévia: todos os canais
// permitidos ligados na frequência padrão. `sino`/`email` = frequência ou null
// (desligado); `canal`/`frequencia` são os derivados, no formato antigo.
export function inscricaoPadrao(alerta) {
  const permitidos = (alerta && alerta.canaisPermitidos) || [];
  const frequencia = frequenciaPadraoDe(alerta);
  const sino = permitidos.indexOf("SINO") !== -1 ? frequencia : null;
  const email = permitidos.indexOf("EMAIL") !== -1 ? frequencia : null;
  return normalizarInscricao({ sino: sino || (email ? null : frequencia), email });
}

// Inscrição vinda do backend ou de uma escolha da tela, no formato completo
// { sino, email, canal, frequencia }. Aceita só {canal, frequencia} (resposta
// antiga) e converte pelo canal.
export function normalizarInscricao(i) {
  const x = i || {};
  let sino = x.sino || null;
  let email = x.email || null;
  if (!sino && !email && x.canal && x.frequencia) {
    sino = x.canal === "SINO" || x.canal === "AMBOS" ? x.frequencia : null;
    email = x.canal === "EMAIL" || x.canal === "AMBOS" ? x.frequencia : null;
  }
  let canal = null;
  if (sino && email) canal = "AMBOS";
  else if (sino) canal = "SINO";
  else if (email) canal = "EMAIL";
  return { sino, email, canal, frequencia: sino || email || null };
}

// Texto curto da inscrição para listas ("Sino na hora · E-mail resumo diário").
export function descreverInscricao(i) {
  const n = normalizarInscricao(i);
  const partes = [];
  if (n.sino) partes.push(`Sino ${(FREQUENCIA_CANAL_LABEL[n.sino] || n.sino).toLowerCase()}`);
  if (n.email) partes.push(`E-mail ${(FREQUENCIA_CANAL_LABEL[n.email] || n.email).toLowerCase()}`);
  return partes.join(" · ");
}

// ── PO1-3389: avisos x tarefas ───────────────────────────────────────────

// Alertas de teste do motor ficam fora das telas de inscrição (são da Everlog,
// disparados por /alertas/teste).
export function ehAlertaDeTeste(alerta) {
  return /^TESTE_/.test(String((alerta && alerta.codigo) || ""));
}

export function ehTarefa(alerta) {
  return !!alerta && alerta.tipo === "TAREFA";
}

// Grupo do aviso na tela, pelo prefixo do código do catálogo.
const GRUPOS_POR_PREFIXO = [
  [/^FATURA_/, "Faturas"],
  [/^(NF|CTE)_/, "Integração de documentos"],
  [/^COTACAO_/, "Cotações"],
  [/^CERTIFICADO_/, "Cadastro"],
  // PO1-3493: alertas do módulo de despacho (envio/etiqueta, lote, coleta).
  [/^(ENVIO|LOTE|COLETA)_/, "Despacho"],
];
export const ORDEM_GRUPOS = ["Faturas", "Integração de documentos", "Cotações", "Cadastro", "Despacho", "Outros"];

export function grupoDoAlerta(alerta) {
  const codigo = String((alerta && alerta.codigo) || "");
  const achado = GRUPOS_POR_PREFIXO.find((g) => g[0].test(codigo));
  return achado ? achado[1] : "Outros";
}

// Tarefa não se desliga: o select de cada canal só tem as frequências permitidas.
export function opcoesFrequenciaTarefa(alerta, canal) {
  return opcoesFrequenciaCanal(alerta, canal).filter((f) => f !== CANAL_DESLIGADO);
}

// "sino + e-mail na hora", "sino na hora + e-mail no resumo diário"...
export function descreverCanais(i) {
  const n = normalizarInscricao(i);
  const txt = (f) => (f === "RESUMO_DIARIO" ? "no resumo diário" : "na hora");
  if (n.sino && n.email && n.sino === n.email) return `sino + e-mail ${txt(n.sino)}`;
  const partes = [];
  if (n.sino) partes.push(`sino ${txt(n.sino)}`);
  if (n.email) partes.push(`e-mail ${txt(n.email)}`);
  return partes.join(" + ");
}

// Rótulo curto para o chip do inscrito: "sino", "e-mail diário", "sino + e-mail".
export function canalCurto(i) {
  const n = normalizarInscricao(i);
  const partes = [];
  if (n.sino) partes.push(n.sino === "RESUMO_DIARIO" ? "sino diário" : "sino");
  if (n.email) partes.push(n.email === "RESUMO_DIARIO" ? "e-mail diário" : "e-mail");
  return partes.join(" + ");
}

const pad2 = (n) => (n < 10 ? `0${n}` : String(n));

// dd/mm/aaaa montado à mão: toLocaleString("pt-BR") não funciona em Node sem ICU
// (testes) e o resultado varia por navegador.
export function formatarData(valor) {
  if (!valor) return "";
  const d = valor instanceof Date ? valor : new Date(valor);
  if (isNaN(d.getTime())) return "";
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
}

export function formatarDataHora(valor) {
  if (!valor) return "";
  const d = valor instanceof Date ? valor : new Date(valor);
  if (isNaN(d.getTime())) return "";
  return `${formatarData(d)} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

// "agora", "há 12 min", "há 1 h", "ontem" ou a data.
export function tempoRelativo(valor, agora) {
  if (!valor) return "";
  const d = valor instanceof Date ? valor : new Date(valor);
  if (isNaN(d.getTime())) return "";
  const ref = agora || new Date();
  const diffMin = Math.floor((ref.getTime() - d.getTime()) / 60000);
  if (diffMin < 1) return "agora";
  if (diffMin < 60) return `há ${diffMin} min`;

  const mesmoDia =
    d.getFullYear() === ref.getFullYear() &&
    d.getMonth() === ref.getMonth() &&
    d.getDate() === ref.getDate();
  if (mesmoDia) return `há ${Math.floor(diffMin / 60)} h`;

  const ontem = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate() - 1);
  if (
    d.getFullYear() === ontem.getFullYear() &&
    d.getMonth() === ontem.getMonth() &&
    d.getDate() === ontem.getDate()
  ) {
    return "ontem";
  }
  return formatarData(d);
}

// O backend manda `link` pronto: caminho do Hub (/app/...) vai por history.push;
// URL absoluta (http...) sai do SPA.
export function abrirLink(link, history) {
  if (!link) return;
  if (/^https?:\/\//i.test(link)) {
    window.location.assign(link);
    return;
  }
  if (history && typeof history.push === "function") history.push(link);
}

export function mensagemErro(e, padrao) {
  const data = e && e.response && e.response.data;
  return (data && data.error) || padrao;
}
